import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, message, Modal, Form, Input, InputNumber, Select, Space, Tooltip, Alert, Popconfirm, Badge } from 'antd';
import { PlusOutlined, EditOutlined, CheckCircleOutlined, ExclamationCircleOutlined, DownloadOutlined, ReloadOutlined, WifiOutlined, ToolOutlined, HistoryOutlined, PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;

interface ServerNode {
  id: number;
  name: string;
  host: string;
  host_domain?: string;
  port: number;
  username: string;
  openvpn_domain_1?: string;
  openvpn_domain_2?: string;
  openvpn_domain_3?: string;
  server_path: string;
  steamcmd_path: string;
  status: string;
  is_local: boolean;
  server_count: number;
  created_at: string;
}

interface SteamCMDStatus {
  installed: boolean;
  path: string;
  message: string;
}

interface SQLite3Status {
  installed: boolean;
  path: string;
  version: string;
  message: string;
}

interface ConnectionStatus {
  connected: boolean;
  message: string;
  osType?: string;
}

const Nodes: React.FC = () => {
  const [nodes, setNodes] = useState<ServerNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [nodeType, setNodeType] = useState<'windows' | 'linux' | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingNode, setEditingNode] = useState<ServerNode | null>(null);
  const [steamcmdStatuses, setSteamcmdStatuses] = useState<Record<number, SteamCMDStatus>>({});
  const [installingSteamcmd, setInstallingSteamcmd] = useState<Record<number, boolean>>({});
  const [sqlite3Statuses, setSqlite3Statuses] = useState<Record<number, SQLite3Status>>({});
  const [installingSQLite3, setInstallingSQLite3] = useState<Record<number, boolean>>({});
  const [connectionStatuses, setConnectionStatuses] = useState<Record<number, ConnectionStatus>>({});
  const [testingConnection, setTestingConnection] = useState<Record<number, boolean>>({});
  const [healthStats, setHealthStats] = useState<any>(null);
  const [repairingNodes, setRepairingNodes] = useState<Record<number, boolean>>({});
  const [healthLogsModalVisible, setHealthLogsModalVisible] = useState(false);
  const [selectedNodeForLogs, setSelectedNodeForLogs] = useState<ServerNode | null>(null);
  const [healthLogs, setHealthLogs] = useState<any[]>([]);
  const [healthLogsLoading, setHealthLogsLoading] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    loadNodes();
    loadHealthStats();
  }, []);

  // 加载健康状态统计
  const loadHealthStats = async () => {
    try {
      const response = await api.get('/admin/nodes/health-stats');
      setHealthStats(response.data);
    } catch (error) {
      console.error('加载健康状态统计失败:', error);
    }
  };

  // 手动检查节点健康状态
  const handleHealthCheck = async (nodeId: number) => {
    try {
      await api.post(`/admin/nodes/${nodeId}/health-check`);
      message.success('节点健康检查完成');
      loadNodes();
      loadHealthStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || '健康检查失败');
    }
  };

  // 手动修复节点
  const handleRepairNode = async (nodeId: number) => {
    setRepairingNodes(prev => ({ ...prev, [nodeId]: true }));
    try {
      await api.post(`/admin/nodes/${nodeId}/repair`);
      message.success('节点修复完成');
      loadNodes();
      loadHealthStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || '节点修复失败');
    } finally {
      setRepairingNodes(prev => ({ ...prev, [nodeId]: false }));
    }
  };

  // 查看健康日志
  const handleViewHealthLogs = async (node: ServerNode) => {
    setSelectedNodeForLogs(node);
    setHealthLogsModalVisible(true);
    setHealthLogsLoading(true);
    
    try {
      const response = await api.get(`/admin/nodes/${node.id}/health-logs`);
      setHealthLogs(response.data.logs);
    } catch (error) {
      message.error('加载健康日志失败');
    } finally {
      setHealthLogsLoading(false);
    }
  };

  // 控制健康检查服务
  const handleHealthServiceControl = async (action: 'start' | 'stop') => {
    try {
      await api.post(`/admin/nodes/health-service/${action}`);
      message.success(`健康检查服务已${action === 'start' ? '启动' : '停止'}`);
      loadHealthStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 获取操作系统类型
  const getOSType = (node: ServerNode): string => {
    if (node.is_local) {
      return 'Local';
    }
    
    // 根据路径判断操作系统类型
    if (node.steamcmd_path.includes('\\') || node.server_path.includes('\\')) {
      return 'Windows';
    }
    
    // 根据主机名判断
    if (node.host && node.host.toLowerCase().includes('windows')) {
      return 'Windows';
    }
    
    return 'Linux';
  };

  // 测试远程节点连接
  const handleTestConnection = async (nodeId: number) => {
    setTestingConnection(prev => ({ ...prev, [nodeId]: true }));
    
    try {
      const response = await api.post(`/admin/nodes/${nodeId}/test-connection`, {}, {
        timeout: 15000 // 15秒超时
      });
      
      const connectionStatus: ConnectionStatus = {
        connected: response.data.connected,
        message: response.data.message,
        osType: response.data.osType
      };
      
      setConnectionStatuses(prev => ({
        ...prev,
        [nodeId]: connectionStatus
      }));
      
      if (connectionStatus.connected) {
        message.success(`连接成功`);
      } else {
        message.error(`连接失败`);
      }
      
    } catch (error: any) {
      const connectionStatus: ConnectionStatus = {
        connected: false,
        message: error.response?.data?.message || '连接超时'
      };
      
      setConnectionStatuses(prev => ({
        ...prev,
        [nodeId]: connectionStatus
      }));
      
      message.error(`连接失败`);
    } finally {
      setTestingConnection(prev => ({ ...prev, [nodeId]: false }));
    }
  };

  const loadNodes = async () => {
    try {
      const response = await api.get('/admin/nodes');
      setNodes(response.data);
      
      // 加载所有节点的SteamCMD状态
      await loadSteamCMDStatuses(response.data);
      // 加载所有节点的SQLite3状态
      await loadSQLite3Statuses(response.data);
    } catch (error) {
      message.error('加载节点列表失败');
      // 设置默认数据避免空白页面
      setNodes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSteamCMDStatuses = async (nodeList: ServerNode[]) => {
    const statusPromises = nodeList.map(async (node) => {
      try {
        const response = await api.get(`/admin/nodes/${node.id}/steamcmd-status`);
        return { nodeId: node.id, status: response.data };
      } catch (error) {
        return { nodeId: node.id, status: { installed: false, path: '', message: '检测失败' } };
      }
    });

    const results = await Promise.all(statusPromises);
    const statusMap: Record<number, SteamCMDStatus> = {};
    results.forEach(({ nodeId, status }) => {
      statusMap[nodeId] = status;
    });
    setSteamcmdStatuses(statusMap);
  };

  const loadSQLite3Statuses = async (nodeList: ServerNode[]) => {
    const statusPromises = nodeList.map(async (node) => {
      try {
        const response = await api.get(`/admin/nodes/${node.id}/sqlite3-status`);
        return { nodeId: node.id, status: response.data };
      } catch (error) {
        return { nodeId: node.id, status: { installed: false, path: '', version: '', message: '检测失败' } };
      }
    });

    const results = await Promise.all(statusPromises);
    const statusMap: Record<number, SQLite3Status> = {};
    results.forEach(({ nodeId, status }) => {
      statusMap[nodeId] = status;
    });
    setSqlite3Statuses(statusMap);
  };

  const handleEditNode = (node: ServerNode) => {
    setEditingNode(node);
    editForm.setFieldsValue({
      name: node.name,
      host: node.host,
      host_domain: node.host_domain,
      port: node.port,
      username: node.username,
      openvpn_domain_1: node.openvpn_domain_1,
      openvpn_domain_2: node.openvpn_domain_2,
      openvpn_domain_3: node.openvpn_domain_3,
      server_path: node.server_path,
      steamcmd_path: node.steamcmd_path,
      is_local: node.is_local
    });
    setEditModalVisible(true);
  };

  const handleUpdateNode = async (values: any) => {
    if (!editingNode) return;
    
    try {
      await api.put(`/admin/nodes/${editingNode.id}`, values);
      message.success('节点配置更新成功');
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingNode(null);
      loadNodes();
    } catch (error: any) {
      message.error(error.response?.data?.message || '更新节点配置失败');
    }
  };

  const handleInstallSteamCMD = async (nodeId: number) => {
    setInstallingSteamcmd(prev => ({ ...prev, [nodeId]: true }));
    
    try {
      // 增加超时时间到2分钟
      const response = await api.post(`/admin/nodes/${nodeId}/install-steamcmd`, {}, {
        timeout: 120000 // 2分钟超时
      });
      
      message.success(response.data.message || 'SteamCMD安装已开始');
      
      // 等待一段时间后重新检测状态
      setTimeout(async () => {
        try {
          const statusResponse = await api.get(`/admin/nodes/${nodeId}/steamcmd-status`);
          setSteamcmdStatuses(prev => ({
            ...prev,
            [nodeId]: statusResponse.data
          }));
          
          if (statusResponse.data.installed) {
            message.success('SteamCMD安装完成！');
          } else {
            message.warning('SteamCMD安装可能仍在进行中，请稍后刷新页面查看状态');
          }
        } catch (error) {
          console.warn('检测SteamCMD状态失败:', error);
        }
      }, 5000); // 5秒后检测状态
      
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        message.warning('SteamCMD安装已开始，请稍后刷新页面查看状态');
      } else {
        message.error(error.response?.data?.message || '安装SteamCMD失败');
      }
    } finally {
      setInstallingSteamcmd(prev => ({ ...prev, [nodeId]: false }));
    }
  };

  const handleRefreshSteamCMDStatus = async (nodeId: number) => {
    try {
      const response = await api.get(`/admin/nodes/${nodeId}/steamcmd-status`);
      setSteamcmdStatuses(prev => ({
        ...prev,
        [nodeId]: response.data
      }));
      
      if (response.data.installed) {
        message.success('SteamCMD已安装');
      } else {
        message.info('SteamCMD未安装');
      }
    } catch (error: any) {
      message.error('检测SteamCMD状态失败');
    }
  };

  const handleInstallSQLite3 = async (nodeId: number) => {
    setInstallingSQLite3(prev => ({ ...prev, [nodeId]: true }));
    
    try {
      // 增加超时时间到2分钟
      const response = await api.post(`/admin/nodes/${nodeId}/install-sqlite3`, {}, {
        timeout: 120000 // 2分钟超时
      });
      
      message.success(response.data.message || 'SQLite3安装已开始');
      
      // 等待一段时间后重新检测状态
      setTimeout(async () => {
        try {
          const statusResponse = await api.get(`/admin/nodes/${nodeId}/sqlite3-status`);
          setSqlite3Statuses(prev => ({
            ...prev,
            [nodeId]: statusResponse.data
          }));
          
          if (statusResponse.data.installed) {
            message.success('SQLite3安装完成！');
          } else {
            message.warning('SQLite3安装可能仍在进行中，请稍后刷新页面查看状态');
          }
        } catch (error) {
          console.warn('检测SQLite3状态失败:', error);
        }
      }, 5000); // 5秒后检测状态
      
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        message.warning('SQLite3安装已开始，请稍后刷新页面查看状态');
      } else {
        message.error(error.response?.data?.message || '安装SQLite3失败');
      }
    } finally {
      setInstallingSQLite3(prev => ({ ...prev, [nodeId]: false }));
    }
  };

  const handleRefreshSQLite3Status = async (nodeId: number) => {
    try {
      const response = await api.get(`/admin/nodes/${nodeId}/sqlite3-status`);
      setSqlite3Statuses(prev => ({
        ...prev,
        [nodeId]: response.data
      }));
      
      if (response.data.installed) {
        message.success('SQLite3已安装');
      } else {
        message.info('SQLite3未安装');
      }
    } catch (error: any) {
      message.error('检测SQLite3状态失败');
    }
  };

  const handleCreateNode = async (values: any) => {
    try {
      // 根据节点类型设置默认值
      const nodeData = {
        ...values,
        is_local: false, // 远程节点
        server_path: nodeType === 'windows' ? 'D:\\gameservers' : '/opt/gameservers',
        steamcmd_path: nodeType === 'windows' ? 'D:\\steamcmd\\steamcmd.exe' : '/opt/steamcmd',
        port: values.port || 22,
        host_domain: values.host_domain || null,
        openvpn_domain_1: values.openvpn_domain_1 || null,
        openvpn_domain_2: values.openvpn_domain_2 || null,
        openvpn_domain_3: values.openvpn_domain_3 || null,
        private_key: null
      };
      
      await api.post('/admin/nodes', nodeData);
      message.success('节点创建成功');
      setCreateModalVisible(false);
      setNodeType(null);
      form.resetFields();
      loadNodes();
    } catch (error: any) {
      message.error(error.response?.data?.message || '创建节点失败');
    }
  };

  const handleStatusChange = async (nodeId: number, newStatus: string) => {
    try {
      await api.put(`/admin/nodes/${nodeId}/status`, { status: newStatus });
      message.success('节点状态更新成功');
      loadNodes();
    } catch (error: any) {
      message.error(error.response?.data?.message || '更新状态失败');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      online: { color: 'green', text: '在线' },
      offline: { color: 'red', text: '离线' },
      maintenance: { color: 'orange', text: '维护中' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status || '未知' };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '节点名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '主机IP/动态域名',
      dataIndex: 'host',
      key: 'host',
      width: 200,
      render: (host: string, record: ServerNode) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{host}</div>
          {record.host_domain && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.host_domain}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '端口',
      dataIndex: 'port',
      key: 'port',
      width: 80,
      render: (port: number) => port || 22,
    },
    {
      title: '类型',
      dataIndex: 'is_local',
      key: 'is_local',
      width: 120,
      render: (isLocal: boolean, record: ServerNode) => {
        const osType = getOSType(record);
        return (
          <Space direction="vertical" size="small">
            <Tag color={isLocal ? 'blue' : 'green'}>
              {isLocal ? '本地' : '远程'}
            </Tag>
            {!isLocal && (
              <Tag color={osType === 'Windows' ? 'cyan' : 'purple'}>
                {osType}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status: string, record: ServerNode) => {
        const connectionStatus = connectionStatuses[record.id];
        
        return (
          <Space direction="vertical" size="small">
            {getStatusTag(status)}
            {!record.is_local && (
              <Space direction="vertical" size="small">
                <Space>
                  <Button
                    size="small"
                    icon={<WifiOutlined />}
                    loading={testingConnection[record.id]}
                    onClick={() => handleTestConnection(record.id)}
                    title="测试连接"
                  >
                    测试连接
                  </Button>
                  {connectionStatus && (
                    <Tag 
                      color={connectionStatus.connected ? 'green' : 'red'} 
                      title={connectionStatus.message}
                    >
                      {connectionStatus.connected ? '已连接' : '连接失败'}
                    </Tag>
                  )}
                </Space>
                <Space>
                  <Button
                    size="small"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleHealthCheck(record.id)}
                    title="健康检查"
                  >
                    健康检查
                  </Button>
                  {record.host_domain && (
                    <Popconfirm
                      title="确定要修复此节点吗？"
                      description="系统将尝试解析动态域名并更新IP地址"
                      onConfirm={() => handleRepairNode(record.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button
                        size="small"
                        icon={<ToolOutlined />}
                        loading={repairingNodes[record.id]}
                        disabled={status === 'online'}
                        title="自动修复"
                      >
                        修复
                      </Button>
                    </Popconfirm>
                  )}
                  <Button
                    size="small"
                    icon={<HistoryOutlined />}
                    onClick={() => handleViewHealthLogs(record)}
                    title="查看健康日志"
                  >
                    日志
                  </Button>
                </Space>
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: 'OpenVPN域名/IP',
      key: 'openvpn_domains',
      width: 200,
      render: (_: any, record: ServerNode) => (
        <div>
          <div style={{ fontSize: '12px', marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>1:</span> {record.openvpn_domain_1 || '-'}
          </div>
          <div style={{ fontSize: '12px', marginBottom: '2px' }}>
            <span style={{ fontWeight: 'bold' }}>2:</span> {record.openvpn_domain_2 || '-'}
          </div>
          <div style={{ fontSize: '12px' }}>
            <span style={{ fontWeight: 'bold' }}>3:</span> {record.openvpn_domain_3 || '-'}
          </div>
        </div>
      ),
    },
    {
      title: '服务器数',
      dataIndex: 'server_count',
      key: 'server_count',
      width: 100,
      render: (count: number) => count || 0,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: '服务器路径',
      dataIndex: 'server_path',
      key: 'server_path',
      width: 150,
      render: (path: string) => (
        <Tooltip title={path}>
          <span className="truncate block max-w-32">{path || '/opt/gameservers'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'SteamCMD状态',
      key: 'steamcmd_status',
      width: 180,
      render: (_: any, record: ServerNode) => {
        const status = steamcmdStatuses[record.id];
        if (!status) return <Tag color="default">检测中...</Tag>;
        
        return (
          <Space direction="vertical" size="small">
            <Space>
              <Tag color={status.installed ? 'green' : 'red'} icon={status.installed ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}>
                {status.installed ? '已安装' : '未安装'}
              </Tag>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRefreshSteamCMDStatus(record.id)}
                title="刷新状态"
              />
            </Space>
            {!status.installed && (
              <Button
                size="small"
                type="primary"
                icon={<DownloadOutlined />}
                loading={installingSteamcmd[record.id]}
                onClick={() => handleInstallSteamCMD(record.id)}
              >
                安装
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: (
        <Space>
          SQLite3状态
          <Tooltip title="商店管理中的玩家金币充值功能必装软件">
            <ExclamationCircleOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
          </Tooltip>
        </Space>
      ),
      key: 'sqlite3_status',
      width: 200,
      render: (_: any, record: ServerNode) => {
        const status = sqlite3Statuses[record.id];
        if (!status) return <Tag color="default">检测中...</Tag>;
        
        return (
          <Space direction="vertical" size="small">
                 <Space>
                   <Tooltip title={status.installed && status.path ? `安装路径: ${status.path}` : undefined}>
                     <Tag color={status.installed ? 'green' : 'red'} icon={status.installed ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}>
                       {status.installed ? '已安装' : '未安装'}
                     </Tag>
                   </Tooltip>
                   <Button
                     size="small"
                     icon={<ReloadOutlined />}
                     onClick={() => handleRefreshSQLite3Status(record.id)}
                     title="刷新状态"
                   />
                 </Space>
            {!status.installed ? (
              <Space direction="vertical" size="small">
                <Button
                  size="small"
                  type="primary"
                  icon={<DownloadOutlined />}
                  loading={installingSQLite3[record.id]}
                  onClick={() => handleInstallSQLite3(record.id)}
                >
                  安装
                </Button>
                <div style={{ fontSize: '10px', color: '#666', textAlign: 'center' }}>
                  商店充值必装
                </div>
              </Space>
            ) : (
              <Space direction="vertical" size="small">
                <Button
                  size="small"
                  type="default"
                  icon={<DownloadOutlined />}
                  loading={installingSQLite3[record.id]}
                  onClick={() => handleInstallSQLite3(record.id)}
                >
                  重装
                </Button>
                {status.version && (
                  <Tag color="blue" style={{ fontSize: '10px' }}>
                    {status.version}
                  </Tag>
                )}
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: ServerNode) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditNode(record)}
          >
            编辑
          </Button>
          <Select
            value={record.status || 'offline'}
            onChange={(value) => handleStatusChange(record.id, value)}
            size="small"
            style={{ width: 100 }}
          >
            <Option value="online">在线</Option>
            <Option value="offline">离线</Option>
            <Option value="maintenance">维护</Option>
          </Select>
        </Space>
      ),
    },
  ];

  return (
    <div className="fade-in">
      {/* 健康状态统计卡片 */}
      {healthStats && (
        <Card className="mb-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold mb-2">节点健康状态</h3>
              <Space size="large">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{healthStats.total_nodes}</div>
                  <div className="text-sm text-gray-600">总节点数</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{healthStats.online_nodes}</div>
                  <div className="text-sm text-gray-600">在线节点</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{healthStats.offline_nodes}</div>
                  <div className="text-sm text-gray-600">离线节点</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{healthStats.maintenance_nodes}</div>
                  <div className="text-sm text-gray-600">维护中</div>
                </div>
              </Space>
            </div>
            <Space>
              <Button
                icon={<PlayCircleOutlined />}
                onClick={() => handleHealthServiceControl('start')}
                title="启动健康检查服务"
              >
                启动服务
              </Button>
              <Button
                icon={<PauseCircleOutlined />}
                onClick={() => handleHealthServiceControl('stop')}
                title="停止健康检查服务"
              >
                停止服务
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadHealthStats}
                title="刷新健康状态"
              >
                刷新
              </Button>
            </Space>
          </div>
        </Card>
      )}

      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">节点管理</h2>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setNodeType('windows');
                setCreateModalVisible(true);
              }}
              style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
            >
              添加Windows节点
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setNodeType('linux');
                setCreateModalVisible(true);
              }}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              添加Linux节点
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={nodes}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个节点`,
          }}
          scroll={{ x: 1300 }}
          locale={{
            emptyText: loading ? '加载中...' : '暂无节点数据，请添加节点或检查数据库连接'
          }}
        />
      </Card>

      {/* 创建节点模态框 */}
      <Modal
        title={`添加${nodeType === 'windows' ? 'Windows' : 'Linux'}节点`}
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          setNodeType(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleCreateNode} layout="vertical">
          <Alert
            message={`${nodeType === 'windows' ? 'Windows' : 'Linux'}节点配置说明`}
            description={
              nodeType === 'windows' 
                ? "Windows远程节点：运行在Windows系统上，使用WinRM连接，需要服务账户权限。"
                : "Linux远程节点：运行在Linux系统上，使用SSH连接，需要root或sudo权限。"
            }
            type="info"
            showIcon
            className="mb-4"
          />
          
          <Form.Item
            name="name"
            label="节点名称"
            rules={[
              { required: true, message: '请输入节点名称' },
              { min: 2, message: '节点名称至少2个字符' },
              { max: 50, message: '节点名称最多50个字符' },
              { pattern: /^[a-zA-Z0-9\u4e00-\u9fa5_-]+$/, message: '节点名称只能包含字母、数字、中文、下划线和连字符' }
            ]}
          >
            <Input placeholder={`输入${nodeType === 'windows' ? 'Windows' : 'Linux'}节点名称，如：${nodeType === 'windows' ? 'Windows-Node-01' : 'Linux-Node-01'}`} />
          </Form.Item>

          <Form.Item
            name="host"
            label="主机地址"
            rules={[{ required: true, message: '请输入主机地址' }]}
          >
            <Input placeholder="如：192.168.1.100 或 example.com" />
          </Form.Item>

          <Form.Item
            name="port"
            label={nodeType === 'windows' ? 'WinRM端口' : 'SSH端口'}
            initialValue={nodeType === 'windows' ? 5985 : 22}
          >
            <InputNumber min={1} max={65535} className="w-full" placeholder={nodeType === 'windows' ? 'WinRM连接端口，默认5985' : 'SSH连接端口，默认22'} />
          </Form.Item>

          <Form.Item 
            name="username" 
            label={nodeType === 'windows' ? '服务账户用户名' : 'SSH用户名'}
            rules={[{ required: true, message: `请输入${nodeType === 'windows' ? '服务账户用户名' : 'SSH用户名'}` }]}
          >
            <Input placeholder={nodeType === 'windows' ? 'Windows服务账户用户名，如：gameserver' : 'SSH登录用户名，如：root'} />
          </Form.Item>

          <Form.Item 
            name="password" 
            label={nodeType === 'windows' ? '服务账户密码' : 'SSH密码'}
            rules={[{ required: true, message: `请输入${nodeType === 'windows' ? '服务账户密码' : 'SSH密码'}` }]}
          >
            <Input.Password placeholder={nodeType === 'windows' ? 'Windows服务账户密码' : 'SSH登录密码'} />
          </Form.Item>

          <Form.Item name="host_domain" label="主机动态域名">
            <Input placeholder="如：node1.example.com 或 dynamic.example.com" />
          </Form.Item>

          <Form.Item name="openvpn_domain_1" label="OpenVPN域名/IP 1">
            <Input placeholder="如：vpn1.example.com 或 10.0.0.1" />
          </Form.Item>

          <Form.Item name="openvpn_domain_2" label="L2TP VPN域名/IP 2">
            <Input placeholder="如：vpn2.example.com 或 10.0.0.2" />
          </Form.Item>

          <Form.Item name="openvpn_domain_3" label="OpenVPN域名/IP 3">
            <Input placeholder="如：vpn3.example.com 或 10.0.0.3" />
          </Form.Item>

          <Alert
            message="路径配置"
            description={
              nodeType === 'windows' 
                ? "Windows路径：游戏服务器路径 D:\\gameservers，SteamCMD路径 D:\\steamcmd\\steamcmd.exe"
                : "Linux路径：游戏服务器路径 /opt/gameservers，SteamCMD路径 /opt/steamcmd"
            }
            type="success"
            showIcon
            className="mb-4"
          />

          <div className="flex justify-end space-x-2 mt-6">
            <Button onClick={() => {
              setCreateModalVisible(false);
              setNodeType(null);
              form.resetFields();
            }}>
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              创建{nodeType === 'windows' ? 'Windows' : 'Linux'}节点
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 编辑节点模态框 */}
      <Modal
        title="编辑节点配置"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setEditingNode(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={editForm} onFinish={handleUpdateNode} layout="vertical">
          <Form.Item
            name="name"
            label="节点名称"
            rules={[{ required: true, message: '请输入节点名称' }]}
          >
            <Input placeholder="输入节点名称，如：主服务器、备用节点等" />
          </Form.Item>

          <Form.Item
            name="host"
            label="主机地址"
            rules={[{ required: true, message: '请输入主机地址' }]}
          >
            <Input placeholder="如：192.168.1.100 或 example.com" />
          </Form.Item>

          <Form.Item
            name="port"
            label="SSH端口"
          >
            <InputNumber min={1} max={65535} className="w-full" placeholder="SSH连接端口，默认22" />
          </Form.Item>

          <Form.Item name="username" label="SSH用户名">
            <Input placeholder="SSH登录用户名（远程节点需要）" />
          </Form.Item>

          <Form.Item name="password" label="SSH密码">
            <Input.Password placeholder="SSH登录密码（远程节点需要）" />
          </Form.Item>

          <Form.Item name="host_domain" label="主机动态域名">
            <Input placeholder="如：node1.example.com 或 dynamic.example.com" />
          </Form.Item>

          <Form.Item name="openvpn_domain_1" label="OpenVPN域名/IP 1">
            <Input placeholder="如：vpn1.example.com 或 10.0.0.1" />
          </Form.Item>

          <Form.Item name="openvpn_domain_2" label="L2TP VPN域名/IP 2">
            <Input placeholder="如：vpn2.example.com 或 10.0.0.2" />
          </Form.Item>

          <Form.Item name="openvpn_domain_3" label="OpenVPN域名/IP 3">
            <Input placeholder="如：vpn3.example.com 或 10.0.0.3" />
          </Form.Item>

          <Form.Item 
            name="server_path" 
            label="服务器路径" 
            rules={[{ required: true, message: '请输入服务器路径' }]}
          >
            <Input placeholder="游戏服务器安装路径，Linux: /opt/gameservers, Windows: D:\gameservers" />
          </Form.Item>

          <Form.Item 
            name="steamcmd_path" 
            label="SteamCMD路径" 
            rules={[{ required: true, message: '请输入SteamCMD路径' }]}
          >
            <Input placeholder="SteamCMD安装路径，Linux: /opt/steamcmd, Windows: D:\steamcmd\steamcmd.exe" />
          </Form.Item>

          <Form.Item name="is_local" label="节点类型">
            <Select onChange={(value) => {
              if (value === false) {
                // 远程节点，设置为Windows默认路径
                editForm.setFieldsValue({
                  server_path: 'D:\\gameservers',
                  steamcmd_path: 'D:\\steamcmd\\steamcmd.exe'
                });
              } else {
                // 本地节点，设置为Linux默认路径
                editForm.setFieldsValue({
                  server_path: '/opt/gameservers',
                  steamcmd_path: '/opt/steamcmd'
                });
              }
            }}>
              <Option value={true}>本地节点 (Linux)</Option>
              <Option value={false}>远程节点 (Windows)</Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end space-x-2 mt-6">
            <Button onClick={() => {
              setEditModalVisible(false);
              editForm.resetFields();
              setEditingNode(null);
            }}>
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              保存配置
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 健康日志模态框 */}
      <Modal
        title={`健康日志 - ${selectedNodeForLogs?.name}`}
        open={healthLogsModalVisible}
        onCancel={() => {
          setHealthLogsModalVisible(false);
          setSelectedNodeForLogs(null);
          setHealthLogs([]);
        }}
        footer={null}
        width={800}
      >
        <Table
          dataSource={healthLogs}
          loading={healthLogsLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条日志`,
          }}
          columns={[
            {
              title: '时间',
              dataIndex: 'created_at',
              key: 'created_at',
              width: 150,
              render: (date: string) => new Date(date).toLocaleString(),
            },
            {
              title: '事件类型',
              dataIndex: 'event_type',
              key: 'event_type',
              width: 120,
              render: (type: string) => {
                const typeMap: Record<string, { color: string; text: string }> = {
                  offline: { color: 'red', text: '离线' },
                  online: { color: 'green', text: '在线' },
                  repair_attempt: { color: 'orange', text: '修复尝试' },
                  repair_success: { color: 'green', text: '修复成功' },
                  repair_failed: { color: 'red', text: '修复失败' },
                  repair_skipped: { color: 'default', text: '跳过修复' },
                  repair_error: { color: 'red', text: '修复错误' },
                  recovered: { color: 'green', text: '自动恢复' },
                };
                const typeInfo = typeMap[type] || { color: 'default', text: type };
                return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
              },
            },
            {
              title: '消息',
              dataIndex: 'message',
              key: 'message',
              render: (message: string) => message || '-',
            },
            {
              title: 'IP变化',
              key: 'ip_change',
              width: 200,
              render: (_: any, record: any) => {
                if (record.old_ip && record.new_ip) {
                  return (
                    <div>
                      <div className="text-red-600">{record.old_ip}</div>
                      <div className="text-gray-400">→</div>
                      <div className="text-green-600">{record.new_ip}</div>
                    </div>
                  );
                }
                return '-';
              },
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default Nodes;
